import { getMediaSrc, getMediaUrl } from '@internal/utils/media/getMediaSrc';
import { MachineMedia, ZohoToolsFile } from '@internal/utils/media/Media';
import {
    ArrowBackIosNew as ArrowBackIcon,
    ArrowForwardIos as ArrowForwardIcon,
    Close as CloseIcon,
    Download as DownloadIcon,
    PlayCircleOutline as PlayIcon,
} from '@mui/icons-material';
import { IconButton, Modal, Typography } from '@mui/material';
import classNames from 'classnames';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';
import { Keyboard, Mousewheel, Navigation, Thumbs, Zoom } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './mediaSliderOverlay.module.scss';

type Props = {
    media: (MachineMedia | ZohoToolsFile)[];
    open: boolean;
    initialSlideIndex: number;
    onClose: () => void;
    onSlideChange?: (activeIndex: number) => void;
    download?: boolean;
    watermarkImages?: boolean;
};

export const MediaSliderOverlay: FC<Props> = ({
    media,
    onClose,
    initialSlideIndex,
    open,
    onSlideChange,
    download,
    watermarkImages,
}) => {
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
    const [activeIndex, setActiveIndex] = useState<number>(initialSlideIndex);
    const videoRefs = useRef<HTMLVideoElement[]>([]);

    const addVideoRef = useCallback((video: HTMLVideoElement | null) => {
        if (video) {
            videoRefs.current.push(video);
        }
    }, []);

    const { thumbnails, items, downloadLinks } = useMemo(
        () => ({
            thumbnails: media.map(entry => {
                if (
                    entry.mime.startsWith('image') ||
                    entry.mime.startsWith('video')
                ) {
                    return {
                        type: 'media',
                        url: getMediaUrl(entry, {
                            ratio: 1,
                            disableMachineWatermark: true,
                            width: 100,
                            height: 100,
                            createVideoThumbnail: true,
                        }),
                        mime: entry.mime,
                    };
                }
                return {
                    type: 'document',
                    url: getMediaUrl(entry),
                };
            }),
            items: media.map(entry => {
                if (entry.mime.startsWith('image')) {
                    return {
                        type: 'image',
                        ...getMediaSrc(entry, {
                            disableMachineWatermark: !watermarkImages,
                            sizeSet: 'extraLarge',
                        }),
                        mime: entry.mime,
                        description:
                            entry.type === 'machine'
                                ? entry.attribute
                                : entry.description,
                    };
                }

                if (entry.mime.startsWith('video')) {
                    return {
                        type: 'video',
                        src: getMediaUrl(entry),
                        mime: entry.mime,
                        description:
                            entry.type === 'machine'
                                ? entry.attribute
                                : entry.description,
                        poster: getMediaUrl(entry, {
                            disableMachineWatermark: true,
                            createVideoThumbnail: true,
                        }),
                    };
                }

                return {
                    type: 'document',
                    src: getMediaUrl(entry),
                };
            }),
            downloadLinks: media.map(entry =>
                getMediaUrl(entry, {
                    download: true,
                }),
            ),
        }),
        [media, watermarkImages],
    );

    const hasDocuments = useMemo(
        () => items.some(item => item.type === 'document'),
        [items],
    );

    const handleSlideChange = useCallback(
        (swiper: SwiperType) => {
            setActiveIndex(swiper.activeIndex);
            onSlideChange?.(swiper.activeIndex);
            videoRefs.current.map(video => video.pause());
        },
        [onSlideChange],
    );

    const showThumbs = thumbnails.length > 1;

    return (
        <Modal open={open} className={styles.modal} onClose={onClose}>
            <div
                className={classNames(styles.wrapper, {
                    [styles.noThumbs]: !showThumbs,
                    [styles.hasDocuments]: hasDocuments,
                })}
            >
                <IconButton
                    className={classNames(styles.button, styles.closeButton)}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>

                <IconButton
                    className={classNames(styles.button, styles.navigationPrev)}
                >
                    <ArrowBackIcon />
                </IconButton>

                <IconButton
                    className={classNames(styles.button, styles.navigationNext)}
                >
                    <ArrowForwardIcon />
                </IconButton>

                {download && (
                    <IconButton
                        className={classNames(
                            styles.button,
                            styles.downloadButton,
                        )}
                        href={downloadLinks[activeIndex]}
                    >
                        <DownloadIcon />
                    </IconButton>
                )}

                <Swiper
                    modules={[Thumbs, Navigation, Zoom, Keyboard, Mousewheel]}
                    spaceBetween={10}
                    navigation={{
                        nextEl: `.${styles.navigationNext}`,
                        prevEl: `.${styles.navigationPrev}`,
                    }}
                    thumbs={{
                        swiper:
                            thumbsSwiper && !thumbsSwiper.destroyed
                                ? thumbsSwiper
                                : null,
                    }}
                    keyboard={{
                        enabled: true,
                    }}
                    mousewheel={{
                        enabled: true,
                    }}
                    zoom={{
                        maxRatio: 3,
                        minRatio: 1,
                        toggle: true,
                        containerClass: 'swiper-zoom-container',
                        zoomedSlideClass: 'swiper-slide-zoomed',
                    }}
                    onSlideChange={handleSlideChange}
                    initialSlide={initialSlideIndex}
                    className={styles.mainSwiper}
                >
                    {items.map(entry => (
                        <SwiperSlide key={entry.src} className={styles.slide}>
                            <div className={styles.slideContent}>
                                {entry.type === 'video' && (
                                    <video
                                        className={styles.image}
                                        controls
                                        height="auto"
                                        width="auto"
                                        preload="none"
                                        poster={entry.poster}
                                        ref={addVideoRef}
                                    >
                                        <source
                                            src={entry.src}
                                            type={
                                                undefined /* we are explicitly not passing a mime type here because imagekit will adjust it dynamically for the browser and the connection */
                                            }
                                        />
                                    </video>
                                )}

                                {entry.type === 'image' && (
                                    <div className="swiper-zoom-container">
                                        <img
                                            loading={
                                                'eager' /* we want the images to be preloaded */
                                            }
                                            alt=""
                                            src={entry.src}
                                            srcSet={
                                                'srcSet' in entry
                                                    ? entry.srcSet
                                                    : undefined
                                            }
                                            className={styles.image}
                                        />
                                    </div>
                                )}

                                {entry.type === 'document' && (
                                    <iframe
                                        className={styles.iframe}
                                        src={`${entry.src}#view=Fit&page=1`}
                                    />
                                )}
                            </div>
                            {1 < 0 && entry.description && (
                                <Typography
                                    variant="body2"
                                    className={styles.description}
                                >
                                    {entry.description}
                                </Typography>
                            )}
                            {entry.type === 'image' && (
                                // although images get eagerly loaded, we want to show a loader during loading
                                <div className="swiper-lazy-preloader  swiper-lazy-preloader-white" />
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper>

                {showThumbs && (
                    <Swiper
                        onSwiper={setThumbsSwiper}
                        spaceBetween={8}
                        slidesPerView="auto"
                        freeMode
                        watchSlidesProgress
                        className={styles.thumbsSwiper}
                    >
                        {thumbnails.map(({ url, mime, type }) => (
                            <SwiperSlide key={url}>
                                {type === 'media' && (
                                    <img
                                        loading="lazy"
                                        alt=""
                                        src={url}
                                        className={styles.content}
                                    />
                                )}
                                {type === 'document' && (
                                    <iframe
                                        className={styles.content}
                                        src={`${url}#view=FitH&page=1&toolbar=0&navpanes=0`}
                                    />
                                )}
                                {mime?.startsWith('video') && (
                                    <PlayIcon className={styles.playIcon} />
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                )}
            </div>
        </Modal>
    );
};

MediaSliderOverlay.displayName = 'MediaSliderOverlay';
